import React, { lazy, Suspense, Component, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './layouts/AppLayout';
import AdminLayout from './layouts/AdminLayout';
import ViewErrorBoundary from './components/ViewErrorBoundary';
import { isNativeApp, isIosShell } from './utils/platform';
import { isIosCommercePath } from './utils/iosCommerceGate';
import { logReactBoundaryError } from './utils/diagnosticUncaught';
import { cacheBustedReload } from './utils/bootRecovery';
import {
  CHUNK_LOAD_MAX_AUTO_RETRIES,
  chunkRetryBudgetRemaining,
  consumeChunkRetryBudget,
  currentRoutePath,
  emitChunkLoadFailedBeacon,
  emitChunkLoadRetryBeacon,
  isDynamicChunkLoadFailure,
  resetChunkRetryBudget,
} from './utils/chunkLoadRetry';

function retryImport(factory) {
  return factory().catch((err) => {
    const failedUrl = isDynamicChunkLoadFailure(err);
    if (!failedUrl) throw err;

    if (!chunkRetryBudgetRemaining(sessionStorage)) {
      emitChunkLoadFailedBeacon(failedUrl, currentRoutePath(), CHUNK_LOAD_MAX_AUTO_RETRIES);
      throw err;
    }

    const retryNumber = consumeChunkRetryBudget(sessionStorage);
    emitChunkLoadRetryBeacon(failedUrl, retryNumber);

    if (retryNumber < CHUNK_LOAD_MAX_AUTO_RETRIES) {
      return new Promise((resolve) => setTimeout(resolve, 800)).then(() => factory());
    }

    cacheBustedReload();
    return new Promise(() => {});
  });
}

const RETRY_BTN = {
  padding: '10px 24px',
  borderRadius: 8,
  background: '#C9A84C',
  color: '#1a1a2e',
  fontWeight: 700,
  fontSize: 14,
  border: 'none',
  cursor: 'pointer',
  marginTop: 8,
};

function ChunkLoadFailureScreen({ onRetry }) {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 40,
      color: 'var(--text-secondary, #999)',
    }}
    >
      <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary, #fff)' }}>
        Couldn&apos;t load this page
      </p>
      <p style={{ fontSize: 14, maxWidth: 400, textAlign: 'center', lineHeight: 1.5 }}>
        This can happen after an update — try again.
      </p>
      <button type="button" onClick={onRetry} style={RETRY_BTN}>
        Retry
      </button>
    </div>
  );
}

const MarketingPage = lazy(() => retryImport(() => import('./pages/MarketingPage')));
const MarketingSoundPage = lazy(() => retryImport(() => import('./pages/marketing/MarketingSoundPage')));
const MarketingLibraryPage = lazy(() => retryImport(() => import('./pages/marketing/MarketingLibraryPage')));
const MarketingEllaPage = lazy(() => retryImport(() => import('./pages/marketing/MarketingEllaPage')));
const MarketingRemasterPage = lazy(() => retryImport(() => import('./pages/marketing/MarketingRemasterPage')));
const MarketingComparePage = lazy(() => retryImport(() => import('./pages/marketing/MarketingComparePage')));
const MarketingStoryPage = lazy(() => retryImport(() => import('./pages/marketing/MarketingStoryPage')));
const MarketingPricingPage = lazy(() => retryImport(() => import('./pages/marketing/MarketingPricingPage')));
const SharedItemPage = lazy(() => retryImport(() => import('./pages/SharedItemPage')));
const PrivacyPolicy = lazy(() => retryImport(() => import('./pages/PrivacyPolicy')));
const TermsOfService = lazy(() => retryImport(() => import('./pages/TermsOfService')));
const CookiePolicy = lazy(() => retryImport(() => import('./pages/CookiePolicy')));
const ResetPasswordPage = lazy(() => retryImport(() => import('./pages/ResetPasswordPage')));
const ManageSubscriptionPage = lazy(() => retryImport(() => import('./pages/ManageSubscriptionPage')));
const BillingPage = lazy(() => retryImport(() => import('./pages/BillingPage')));
const HelpPage = lazy(() => retryImport(() => import('./pages/HelpPage')));
const SubscribePage = lazy(() => retryImport(() => import('./pages/SubscribePage')));
const LoginPage = lazy(() => retryImport(() => import('./pages/LoginPage')));
const WelcomeBackPage = lazy(() => retryImport(() => import('./pages/WelcomeBackPage')));
const OnboardingWizard = lazy(() => retryImport(() => import('./components/OnboardingWizard')));

import HomePage from './pages/HomePage';
import { LocalAlbumLegacyRedirect } from './pages/LocalAlbumPage';
const ArtistPage = lazy(() => retryImport(() => import('./pages/ArtistPage')));
const PersonPage = lazy(() => retryImport(() => import('./pages/PersonPage')));
const AlbumPage = lazy(() => retryImport(() => import('./pages/AlbumPage')));
const BrowsePage = lazy(() => retryImport(() => import('./pages/BrowsePage')));
// LibraryPage deleted — /app/library redirects to /app/local-library
const GenresPage = lazy(() => retryImport(() => import('./pages/GenresPage')));
const GenrePage = lazy(() => retryImport(() => import('./pages/GenrePage')));
const RadioPage = lazy(() => retryImport(() => import('./pages/RadioPage')));
const EllaRadioPage = lazy(() => retryImport(() => import('./pages/MaestroRadioPage')));
const OctaveRadioPage = lazy(() => retryImport(() => import('./pages/OctaveRadioPage')));
const CommunityPage = lazy(() => retryImport(() => import('./pages/CommunityPage')));
const QobuzPage = lazy(() => retryImport(() => import('./pages/QobuzPage')));
const FavoritesPage = lazy(() => retryImport(() => import('./pages/FavoritesPage')));
const ValetPage = lazy(() => retryImport(() => import('./pages/ValetPage')));
const PlaylistPage = lazy(() => retryImport(() => import('./pages/PlaylistPage')));
const AudiophilePlaylistsPage = lazy(() => retryImport(() => import('./pages/AudiophilePlaylistsPage')));
const GenrePlaylistsPage = lazy(() => retryImport(() => import('./pages/GenrePlaylistsPage')));
const MoodPlaylistsPage = lazy(() => retryImport(() => import('./pages/MoodPlaylistsPage')));
const SeasonsPlaylistsPage = lazy(() => retryImport(() => import('./pages/SeasonsPlaylistsPage')));
const MyPlaylistsPage = lazy(() => retryImport(() => import('./pages/MyPlaylistsPage')));
const SmartPlaylistPage = lazy(() => retryImport(() => import('./pages/SmartPlaylistPage')));
const FavoritesPlaylistsPage = lazy(() => retryImport(() => import('./pages/FavoritesPlaylistsPage')));
const HistoryPage = lazy(() => retryImport(() => import('./pages/HistoryPage')));
const SupportPage = lazy(() => retryImport(() => import('./pages/SupportPage')));
const AdminPage = lazy(() => retryImport(() => import('./pages/admin/AdminPage')));
const AdminControlCenter = lazy(() => retryImport(() => import('./pages/AdminControlCenter')));
const LocalLibraryPage = lazy(() => retryImport(() => import('./pages/LocalLibraryPage')));
const StreamAlbumsPage = lazy(() => retryImport(() => import('./pages/StreamAlbumsPage')));
const StreamArtistsPage = lazy(() => retryImport(() => import('./pages/StreamArtistsPage')));
const DownloadPage = lazy(() => retryImport(() => import('./pages/DownloadPage')));
const DeviceSetupPage = lazy(() => retryImport(() => import('./pages/DeviceSetupPage')));
const PrismPage = lazy(() => retryImport(() => import('./prism/PrismPage')));
const AccountPage = lazy(() => retryImport(() => import('./pages/AccountPage')));
const OpsControlCenter = lazy(() => retryImport(() => import('./pages/OpsControlCenter')));
const ReleaseChecklistPage = lazy(() => retryImport(() => import('./pages/ReleaseChecklistPage')));
const GoldenBehaviorsPage = lazy(() => retryImport(() => import('./pages/GoldenBehaviorsPage')));
const ShipMonitorPage = lazy(() => retryImport(() => import('./pages/ShipMonitorPage')));
const AdminDevicesPage = lazy(() => retryImport(() => import('./pages/admin/DevicesPage')));
const AdminDiagnosticsPage = lazy(() => retryImport(() => import('./pages/admin/DiagnosticsPage')));
const FleetDiagnosticsPage = lazy(() => retryImport(() => import('./pages/admin/FleetDiagnosticsPage')));

function PageFallback() {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, []);
  if (timedOut) {
    return (
      <ChunkLoadFailureScreen
        onRetry={() => {
          resetChunkRetryBudget(sessionStorage);
          cacheBustedReload();
        }}
      />
    );
  }
  return (
    <div style={{ minHeight: '60vh', background: 'var(--bg-primary, #ffffff)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ width: 40, height: 40, border: '3px solid rgba(201,168,76,0.2)', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, chunkFailure: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ChunkErrorBoundary]', error?.message);

    const msg = String(error?.message || '');
    const renderLoop =
      msg.includes('185') || msg.includes('Maximum update depth') || msg.includes('Too many re-renders');
    logReactBoundaryError(error, info, { renderLoop });
    if (renderLoop) {
      console.error('[ChunkErrorBoundary] Render loop detected — not retrying');
      return;
    }

    const failedUrl = isDynamicChunkLoadFailure(error);
    if (failedUrl) {
      emitChunkLoadFailedBeacon(failedUrl, currentRoutePath(), CHUNK_LOAD_MAX_AUTO_RETRIES);
      this.setState({ chunkFailure: true });
    }
  }
  render() {
    if (this.state.hasError) {
      if (this.state.chunkFailure) {
        return (
          <ChunkLoadFailureScreen
            onRetry={() => {
              resetChunkRetryBudget(sessionStorage);
              cacheBustedReload();
            }}
          />
        );
      }
      return (
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 40, color: 'var(--text-secondary, #999)' }}>
          <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary, #fff)' }}>Something went wrong</p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null, chunkFailure: false })}
            style={RETRY_BTN}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SuspenseWrap({ children }) {
  return <ChunkErrorBoundary><Suspense fallback={<PageFallback />}>{children}</Suspense></ChunkErrorBoundary>;
}

/** Route-level view boundary — isolates one screen without taking down the shell. */
function ViewRoute({ view, children }) {
  return (
    <SuspenseWrap>
      <ViewErrorBoundary viewName={view}>
        {children}
      </ViewErrorBoundary>
    </SuspenseWrap>
  );
}

/** Paths allowed in native shells outside /app (billing handoff — #325; support — #340). */
export const NATIVE_SHELL_SUBSCRIBE_PATH = '/subscribe';
export const NATIVE_SHELL_SUPPORT_PATH = '/help';

function isNativeShellMarketingAllowed(pathname) {
  if (isIosShell()) {
    return pathname === NATIVE_SHELL_SUPPORT_PATH || pathname === '/support';
  }
  return pathname === NATIVE_SHELL_SUBSCRIBE_PATH
    || pathname === NATIVE_SHELL_SUPPORT_PATH
    || pathname === '/support';
}

/** iOS shell must never land on standalone commerce routes (#408). */
function IosCommerceRouteGate({ children }) {
  if (isIosShell()) {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (isIosCommercePath(path)) {
      return <Navigate to="/app" replace />;
    }
  }
  return children;
}

function HelpRoute({ children }) {
  if (isNativeApp()) {
    return <Navigate to="/app/support" replace />;
  }
  return <SuspenseWrap>{children}</SuspenseWrap>;
}

/** Native/Tauri/iOS shells must never land on marketing — /app AuthView handles login. */
function NativeMarketingGate({ children }) {
  if (isNativeApp()) {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (!isNativeShellMarketingAllowed(path)) {
      return <Navigate to="/app" replace />;
    }
  }
  return children;
}

function MarketingRoute({ children }) {
  return <NativeMarketingGate><SuspenseWrap>{children}</SuspenseWrap></NativeMarketingGate>;
}

function SubscribeRoute({ children }) {
  return <IosCommerceRouteGate><SuspenseWrap>{children}</SuspenseWrap></IosCommerceRouteGate>;
}

class RadioErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this._retries = 0;
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error('[RadioErrorBoundary]', error?.message);
    if (this._retries < 3) {
      this._retries++;
      setTimeout(() => this.setState({ hasError: false }), 400 * this._retries);
      return;
    }
  }
  render() {
    if (this.state.hasError) {
      if (this._retries <= 3) {
        return (
          <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(201,168,76,0.2)', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        );
      }
      return (
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 40 }}>
          <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary, #fff)' }}>Radio had a hiccup</p>
          <p style={{ fontSize: 14, color: 'var(--text-secondary, #999)', maxWidth: 400, textAlign: 'center', lineHeight: 1.5 }}>
            Try going back and reopening the station.
          </p>
          <button
            onClick={() => { this._retries = 0; this.setState({ hasError: false }); }}
            style={{ padding: '10px 24px', borderRadius: 8, background: '#C9A84C', color: '#1a1a2e', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', marginTop: 8 }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function isMarketingSite() {
  return false;
}

const APP_ORIGIN = 'https://maestrodesktop.com';

function isPagesPreviewHost() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return host.endsWith('.pages.dev');
}

function RedirectToApp() {
  useEffect(() => {
    if (isPagesPreviewHost()) return;
    const dest = APP_ORIGIN + window.location.pathname + window.location.search + window.location.hash;
    window.location.replace(dest);
  }, []);
  return null;
}

export default function AppRoutes() {
  useEffect(() => { resetChunkRetryBudget(sessionStorage); }, []);
  const onMarketing = isMarketingSite();

  return (
    <Routes>
      {/* Logged-in users skip to /app; native shells always use /app (AuthView when logged out) */}
      <Route path="/" element={<MarketingRoute><MarketingPage /></MarketingRoute>} />
      <Route path="/sound" element={<MarketingRoute><MarketingSoundPage /></MarketingRoute>} />
      <Route path="/library" element={<MarketingRoute><MarketingLibraryPage /></MarketingRoute>} />
      <Route path="/ella" element={<MarketingRoute><MarketingEllaPage /></MarketingRoute>} />
      <Route path="/remaster" element={<MarketingRoute><MarketingRemasterPage /></MarketingRoute>} />
      <Route path="/compare" element={<MarketingRoute><MarketingComparePage /></MarketingRoute>} />
      <Route path="/story" element={<MarketingRoute><MarketingStoryPage /></MarketingRoute>} />
      <Route path="/pricing" element={<MarketingRoute><MarketingPricingPage /></MarketingRoute>} />
      <Route path="/login" element={isNativeApp() ? <Navigate to="/app" replace /> : <SuspenseWrap><LoginPage /></SuspenseWrap>} />
      <Route path="/signin" element={isNativeApp() ? <Navigate to="/app" replace /> : <SuspenseWrap><LoginPage /></SuspenseWrap>} />
      <Route path="/auth" element={isNativeApp() ? <Navigate to="/app" replace /> : <SuspenseWrap><LoginPage /></SuspenseWrap>} />
      <Route path="/settings" element={<Navigate to={isNativeApp() ? "/app" : "/download"} replace />} />
      <Route path="/shared/:code" element={<SuspenseWrap><SharedItemPage /></SuspenseWrap>} />
      <Route path="/privacy" element={<SuspenseWrap><PrivacyPolicy /></SuspenseWrap>} />
      <Route path="/terms" element={<SuspenseWrap><TermsOfService /></SuspenseWrap>} />
      <Route path="/cookies" element={<SuspenseWrap><CookiePolicy /></SuspenseWrap>} />
      <Route path="/reset-password" element={<SuspenseWrap><ResetPasswordPage /></SuspenseWrap>} />
      <Route path="/download" element={<SuspenseWrap><DownloadPage /></SuspenseWrap>} />
      <Route path="/manage-subscription" element={<IosCommerceRouteGate><SuspenseWrap><ManageSubscriptionPage /></SuspenseWrap></IosCommerceRouteGate>} />
      <Route path="/billing" element={<IosCommerceRouteGate><SuspenseWrap><BillingPage /></SuspenseWrap></IosCommerceRouteGate>} />
      <Route path="/help" element={<HelpRoute><HelpPage /></HelpRoute>} />
      <Route path="/support" element={isNativeApp() ? <Navigate to="/app/support" replace /> : <Navigate to="/help" replace />} />
      <Route path="/subscribe" element={<SubscribeRoute><SubscribePage /></SubscribeRoute>} />
      <Route path="/welcome-back" element={<IosCommerceRouteGate><SuspenseWrap><WelcomeBackPage /></SuspenseWrap></IosCommerceRouteGate>} />

      {/* Marketing-only mode: redirect app/auth routes to app origin (currently unused) */}
      {onMarketing ? (
        <>
          <Route path="/welcome" element={<RedirectToApp />} />
          <Route path="/onboarding/*" element={<RedirectToApp />} />
          <Route path="/app/*" element={<RedirectToApp />} />
          <Route path="/app" element={<RedirectToApp />} />
        </>
      ) : (
        <>
          <Route path="/welcome" element={<SuspenseWrap><OnboardingWizard /></SuspenseWrap>} />
          <Route path="/onboarding/*" element={<SuspenseWrap><OnboardingWizard /></SuspenseWrap>} />
          {/* Ops dashboard — standalone admin layout, no sidebar/miniplayer */}
          <Route path="/ops" element={<AdminLayout />}>
            <Route index element={<Navigate to="/ops/control-center" replace />} />
            <Route path="control-center" element={<ViewRoute view="ops"><OpsControlCenter /></ViewRoute>} />
            <Route path="golden-behaviors" element={<ViewRoute view="ops-golden-behaviors"><GoldenBehaviorsPage /></ViewRoute>} />
            <Route path="ship-monitor" element={<ViewRoute view="ops-ship-monitor"><ShipMonitorPage /></ViewRoute>} />
            <Route path="release-checklist" element={<ViewRoute view="ops-release-checklist"><ReleaseChecklistPage /></ViewRoute>} />
            <Route path="*" element={<Navigate to="/ops/control-center" replace />} />
          </Route>

          {/* Admin — standalone layout, bypasses AppLayout platform gate */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<SuspenseWrap><AdminPage /></SuspenseWrap>} />
            <Route path="control-center" element={<SuspenseWrap><AdminControlCenter /></SuspenseWrap>} />
            <Route path="devices" element={<SuspenseWrap><AdminDevicesPage /></SuspenseWrap>} />
            <Route path="diagnostics" element={<SuspenseWrap><AdminDiagnosticsPage /></SuspenseWrap>} />
            <Route path="fleet-diagnostics" element={<SuspenseWrap><FleetDiagnosticsPage /></SuspenseWrap>} />
            <Route path="*" element={<SuspenseWrap><AdminPage /></SuspenseWrap>} />
          </Route>

          <Route path="/app/download" element={<Navigate to="/download" replace />} />
          <Route path="/app/admin/control-center" element={<Navigate to="/admin/control-center" replace />} />
          <Route path="/app/admin/*" element={<Navigate to="/admin" replace />} />
          <Route path="/app/admin" element={<Navigate to="/admin" replace />} />
          <Route path="/app" element={<AppLayout />}>
            <Route index element={<HomePage />} />
            <Route path="artist/:id" element={<SuspenseWrap><ArtistPage /></SuspenseWrap>} />
            <Route path="person/:id" element={<SuspenseWrap><PersonPage /></SuspenseWrap>} />
            <Route path="album/:id" element={<SuspenseWrap><AlbumPage /></SuspenseWrap>} />
            <Route path="browse" element={<SuspenseWrap><BrowsePage /></SuspenseWrap>} />
            <Route path="library" element={<Navigate to="/app/local-library" replace />} />
            <Route path="local-library" element={<ViewRoute view="local-library"><LocalLibraryPage /></ViewRoute>} />
            <Route path="local-album/:key" element={<LocalAlbumLegacyRedirect />} />
            <Route path="genres" element={<SuspenseWrap><GenresPage /></SuspenseWrap>} />
            <Route path="genre/:key" element={<SuspenseWrap><GenrePage /></SuspenseWrap>} />
            <Route path="radio" element={<SuspenseWrap><RadioErrorBoundary><RadioPage /></RadioErrorBoundary></SuspenseWrap>} />
            <Route path="ella-radio" element={<SuspenseWrap><RadioErrorBoundary><EllaRadioPage /></RadioErrorBoundary></SuspenseWrap>} />
            <Route path="maestro-radio" element={<SuspenseWrap><RadioErrorBoundary><EllaRadioPage /></RadioErrorBoundary></SuspenseWrap>} />
            <Route path="octave-radio" element={<SuspenseWrap><RadioErrorBoundary><OctaveRadioPage /></RadioErrorBoundary></SuspenseWrap>} />
            <Route path="community" element={<SuspenseWrap><CommunityPage /></SuspenseWrap>} />
            <Route path="qobuz/*" element={<SuspenseWrap><QobuzPage /></SuspenseWrap>} />
            <Route path="favorites" element={<SuspenseWrap><FavoritesPage /></SuspenseWrap>} />
            <Route path="history" element={<SuspenseWrap><HistoryPage /></SuspenseWrap>} />
            <Route path="support" element={<SuspenseWrap><SupportPage /></SuspenseWrap>} />
            <Route path="valet/:subView" element={<SuspenseWrap><ValetPage /></SuspenseWrap>} />
            <Route path="audiophile-playlists" element={<SuspenseWrap><AudiophilePlaylistsPage /></SuspenseWrap>} />
            <Route path="genre-playlists" element={<SuspenseWrap><GenrePlaylistsPage /></SuspenseWrap>} />
            <Route path="mood-playlists" element={<SuspenseWrap><MoodPlaylistsPage /></SuspenseWrap>} />
            <Route path="season-playlists" element={<SuspenseWrap><SeasonsPlaylistsPage /></SuspenseWrap>} />
            <Route path="my-playlists" element={<SuspenseWrap><MyPlaylistsPage /></SuspenseWrap>} />
            <Route path="smart-playlists" element={<SuspenseWrap><SmartPlaylistPage /></SuspenseWrap>} />
            <Route path="smart-playlist/:id" element={<SuspenseWrap><SmartPlaylistPage /></SuspenseWrap>} />
            <Route path="favorite-playlists" element={<SuspenseWrap><FavoritesPlaylistsPage /></SuspenseWrap>} />
            <Route path="devices" element={<ViewRoute view="devices"><DeviceSetupPage /></ViewRoute>} />
            <Route path="verified-devices" element={<Navigate to="/app/devices" replace />} />
            <Route path="prism" element={<SuspenseWrap><PrismPage /></SuspenseWrap>} />
            <Route path="stream-albums" element={<SuspenseWrap><StreamAlbumsPage /></SuspenseWrap>} />
            <Route path="stream-artists" element={<SuspenseWrap><StreamArtistsPage /></SuspenseWrap>} />
            <Route path="playlist/:id" element={<SuspenseWrap><PlaylistPage /></SuspenseWrap>} />
            <Route path="account" element={<SuspenseWrap><AccountPage /></SuspenseWrap>} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Route>
        </>
      )}

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
